import { Checkbox, Col, InputNumber, Row } from 'antd/lib';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import TextArea from 'antd/lib/input/TextArea';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Typography from 'antd/lib/typography';
import { PjmAgreementType, PjmGeneratorFuelType, PjmGeneratorPermitStatus, PjmGeneratorPermitType, PjmGeneratorType } from 'src/domain/pjm/dto/enums';
import { PjmGeneratorInfo } from 'src/domain/pjm/dto/PjmGeneratorInfo';

const generatorTypeOptions = Object.values(PjmGeneratorType).map(it => ({ label: it, value: it }));

const fuelTypeOptions = Object.values(PjmGeneratorFuelType).map(it => ({ label: it, value: it }));

const permitStatusOptions = Object.values(PjmGeneratorPermitStatus).map(it => ({ label: it, value: it }));

const permitTypeOptions = Object.values(PjmGeneratorPermitType).map(it => ({ label: it, value: it }));

const agreementTypeOptions = Object.values(PjmAgreementType).map(it => ({ label: it, value: it }));

interface LocationGeneratorModalProps {
    value: PjmGeneratorInfo & { _id: string };
    onClose: (actionTaken: boolean) => void;
    onChange: (data: PjmGeneratorInfo & { _id: string }) => void;
}

export function LocationGeneratorModal({ value, onClose, onChange }: LocationGeneratorModalProps) {
    const [form] = Form.useForm<any>();

    const isNew = !value.name;

    if (typeof value?.['order202c'] === 'undefined') {
        value['order202c'] = false;
    }

    const currentYear = new Date().getFullYear();
    const vintageOptions = [];
    const retrofitYearOptions = [];
    for (let year = currentYear; year >= 1950; year--) {
        vintageOptions.push({ label: year, value: year });
        retrofitYearOptions.push({ label: year, value: year });
    }

    function onFormFinish(data: PjmGeneratorInfo & { _id: string }) {
        console.info('LocationGeneratorModal onFormFinish', data);
        onChange(data);
    }

    return (
        <Modal
            title={isNew ? 'Add Generator' : 'Edit Generator'}
            className="pjm-location-generator-modal"
            open
            destroyOnHidden
            maskClosable={false}
            keyboard={false}
            onCancel={() => onClose(false)}
            width="500px"
            footer={[
                <Button key="pjm-location-generator-modal-cancel" onClick={() => onClose(false)}>
                    Cancel
                </Button>,
                <Button key="pjm-location-generator-modal-submit" type="primary" onClick={form.submit}>
                    Save
                </Button>,
            ]}
        >
            <Form form={form} name="pjm-location-generator-form" preserve={false} layout="vertical" onFinish={onFormFinish} initialValues={value}>
                <Form.Item name={['id']} hidden>
                    <Input disabled />
                </Form.Item>
                <Form.Item name={['_id']} hidden>
                    <Input disabled />
                </Form.Item>
                <Form.Item
                    name={['name']}
                    label={<Typography.Text strong>Name</Typography.Text>}
                    hasFeedback
                    rules={[
                        { required: true, message: 'Please enter name!' },
                        { max: 50, message: 'Number of characters should be less than 50' },
                    ]}
                >
                    <Input placeholder="Generator name" />
                </Form.Item>
                <Form.Item
                    name={['nonRetailBtmg']}
                    label={<Typography.Text strong>Non-Retail BTMG</Typography.Text>}
                    hasFeedback
                    valuePropName="checked"
                >
                    <Checkbox></Checkbox>
                </Form.Item>
                <Row gutter={8} align="top">
                    <Col span={12}>
                        <Form.Item
                            name={['maxOutput']}
                            label={<Typography.Text strong>Max Output (kW)</Typography.Text>}
                            hasFeedback
                            rules={[{ required: true, message: 'Please enter value!' }]}
                        >
                            <InputNumber style={{ width: '100px' }} min={0} max={500000} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name={['nameplate']}
                            label={<Typography.Text strong>Nameplate (kW)</Typography.Text>}
                            hasFeedback
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, nameplate) {
                                        const maxOutput = getFieldValue(['maxOutput']);

                                        if (nameplate && maxOutput && nameplate < maxOutput) {
                                            return Promise.reject(new Error(`Nameplate must be greater than or equal to Max Output!`));
                                        }

                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <InputNumber style={{ width: '100px' }} min={0} max={500000} />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item
                    name={['backupGeneratorOnly']}
                    label={<Typography.Text strong>Backup Generator Only</Typography.Text>}
                    hasFeedback
                    valuePropName="checked"
                >
                    <Checkbox></Checkbox>
                </Form.Item>
                <Form.Item
                    name={['generatorType']}
                    label={<Typography.Text strong>Generator Type</Typography.Text>}
                    hasFeedback
                    rules={[{ required: true, message: 'Please select generator type!' }]}
                >
                    <Select
                        showSearch
                        autoClearSearchValue
                        optionFilterProp="label"
                        placeholder="Select Generator Type"
                        options={generatorTypeOptions}
                    />
                </Form.Item>

                <Form.Item
                    name={['fuelType']}
                    label={<Typography.Text strong>Fuel Type</Typography.Text>}
                    hasFeedback
                    rules={[{ required: true, message: 'Please select fuel type!' }]}
                >
                    <Select showSearch autoClearSearchValue optionFilterProp="label" placeholder="Select Fuel Type" options={fuelTypeOptions} />
                </Form.Item>
                <Row gutter={8} align="top">
                    <Col span={12}>
                        <Form.Item
                            name={['vintage']}
                            label={<Typography.Text strong>Vintage</Typography.Text>}
                            hasFeedback
                            rules={[{ required: true, message: 'Please select vintage year!' }]}
                        >
                            <Select
                                showSearch
                                autoClearSearchValue
                                optionFilterProp="label"
                                placeholder="Select vintage year"
                                options={vintageOptions}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name={['retrofitYear']} label={<Typography.Text strong>Retrofit Year</Typography.Text>}>
                            <Select
                                showSearch
                                autoClearSearchValue
                                optionFilterProp="label"
                                placeholder="Select retrofit year"
                                options={retrofitYearOptions}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item
                    name={['permitStatus']}
                    label={<Typography.Text strong>Permit Status</Typography.Text>}
                    hasFeedback
                    rules={[{ required: true, message: 'Please select permit status!' }]}
                >
                    <Select
                        showSearch
                        autoClearSearchValue
                        optionFilterProp="label"
                        placeholder="Select permit status"
                        options={permitStatusOptions}
                    />
                </Form.Item>
                <Form.Item
                    name={['permitType']}
                    label={<Typography.Text strong>Permit Type</Typography.Text>}
                    hasFeedback
                    rules={[
                        {
                            required: true,
                            message: 'Please select permit type!',
                        },
                        ({ getFieldValue }) => ({
                            validator(_, permitType) {
                                const permitStatus = getFieldValue(['permitStatus']);

                                if (permitStatus === PjmGeneratorPermitStatus.NONE) {
                                    return Promise.reject(new Error(`Permit status ${permitStatus} and this permit type cannot be selected!`));
                                }
                                if (
                                    permitType === PjmGeneratorPermitType.NONE &&
                                    [PjmGeneratorPermitStatus.AVAILABLE, PjmGeneratorPermitStatus.IN_PROGRESS].includes(permitStatus)
                                ) {
                                    return Promise.reject(
                                        new Error(`Permit type ${permitType} and permit status ${permitStatus} cannot be selected!`)
                                    );
                                }

                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <Select showSearch autoClearSearchValue optionFilterProp="label" placeholder="Select permit type" options={permitTypeOptions} />
                </Form.Item>
                <Form.Item name={['order202c']} label={<Typography.Text strong>202c Specified Resource?</Typography.Text>} valuePropName="checked">
                    <Checkbox></Checkbox>
                </Form.Item>
                <Form.Item
                    name={['agreementType']}
                    label={<Typography.Text strong>Agreement Type</Typography.Text>}
                    hasFeedback
                    rules={[{ required: true, message: 'Please select agreement type!' }]}
                >
                    <Select
                        showSearch
                        autoClearSearchValue
                        optionFilterProp="label"
                        placeholder="Select agreement type"
                        options={agreementTypeOptions}
                    />
                </Form.Item>
                <Form.Item
                    name={['injectionRight']}
                    label={<Typography.Text strong>Injection Rights (MW) (Required for ISA and WMPA)</Typography.Text>}
                    rules={[
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (value == null && [PjmAgreementType.ISA, PjmAgreementType.WMPA].includes(getFieldValue(['agreementType']))) {
                                    return Promise.reject(new Error('Please enter value!'));
                                }
                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <InputNumber style={{ width: '100px' }} min={0} max={9999.999} />
                </Form.Item>
                <Form.Item name={['note']} label={<Typography.Text strong>Note</Typography.Text>}>
                    <TextArea rows={4} maxLength={500} showCount />
                </Form.Item>
            </Form>
        </Modal>
    );
}
